import { Link } from "@inertiajs/react"

import AdminLayout from "../../../shared/AdminLayout"
import LinkPills from "../../links/pages/LinkPills"
import type { LinkProp } from "../../links/pages/LinkPills"

interface AdminDayItemRow {
  id: number
  time: string
  title: string
  description: string | null
  // t018 `impl-pills` (FR-19, FR-34): every link of the item, previewed
  // as non-interactive pills — replaces the pre-t018 single `url`/
  // `linkLabel` pair.
  links: LinkProp[]
  href: string
}

interface AdminDayProps {
  heading: { title: string; subtitle: string }
  editHref: string
  newItemHref: string
  items: AdminDayItemRow[]
  labels: { back: string; edit: string; addItem: string }
}

const ROW_CLASSES = "flex items-start gap-3 border-b border-gray-100 py-3"
const LAST_ROW_CLASSES = "flex items-start gap-3 py-3"

interface ItemRowProps {
  item: AdminDayItemRow
  isLast: boolean
}

// A tap-to-edit row (FR-16, docs/design/admin.html state 2): time, title
// and the optional description line. One interactive zone per row (the
// whole row is the Link, chevron decorative) -- deletion lives in the
// item form, never a trash icon here (docs/design/admin.html's own
// behavioural note).
function ItemRow({ item, isLast }: ItemRowProps) {
  return (
    <li>
      <Link href={item.href} className={isLast ? LAST_ROW_CLASSES : ROW_CLASSES}>
        <div className="w-12 shrink-0 text-right text-[15px] font-semibold leading-[22px] text-gray-900 tabular-nums">
          {item.time}
        </div>
        <div className="flex min-w-0 grow flex-col gap-0.5">
          <div className="text-[15px] font-semibold leading-[22px] text-gray-900">{item.title}</div>
          {item.description !== null && (
            <div className="text-[13px] leading-[18px] text-gray-500">{item.description}</div>
          )}
          {/* t018 `impl-pills` (FR-34): every link previewed as a
              non-interactive pill, the same row the public day page
              renders — the admin sees what the family will get. */}
          <LinkPills links={item.links} variant="accent" interactive={false} />
        </div>
        <svg
          aria-hidden="true"
          className="mt-0.5 shrink-0 text-gray-400"
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
        >
          <path d="M6 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </Link>
    </li>
  )
}

// The admin day screen (t009, `Schedule::AdminDaysController#show`):
// implements docs/design/admin.html's "State 2" verbatim -- the back
// link to the admin root («Все дни», distinct from the label form's own
// back link, which is the day's own name), the heading with its
// «Изменить» link to the label form, the day's items in time order
// (already ordered server-side, `Schedule::Day#schedule_items`), and the
// dashed «+ Добавить пункт» entry to the new-item form. FR-14: no
// create-day or delete-day control exists anywhere on this screen.
export default function AdminDay({ heading, editHref, newItemHref, items, labels }: AdminDayProps) {
  return (
    <AdminLayout>
      <Link href="/admin" className="flex items-center gap-1.5 text-sm text-accent">
        <svg aria-hidden="true" width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path
            d="M10 3.5L5.5 8l4.5 4.5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        {labels.back}
      </Link>
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-col gap-0.5">
          <h2 className="text-[22px] font-bold leading-7 text-gray-900">{heading.title}</h2>
          <p className="text-[13px] leading-[18px] text-gray-500">{heading.subtitle}</p>
        </div>
        <Link href={editHref} className="pt-1 text-[13px] font-medium leading-[18px] text-accent">
          {labels.edit}
        </Link>
      </div>
      <ol className="flex flex-col">
        {items.map((item, index) => (
          <ItemRow key={item.id} item={item} isLast={index === items.length - 1} />
        ))}
      </ol>
      <Link
        href={newItemHref}
        className="flex h-11 items-center justify-center gap-1.5 rounded-xl border-[1.5px] border-dashed border-gray-300 text-sm font-medium text-accent"
      >
        {labels.addItem}
      </Link>
    </AdminLayout>
  )
}
